import type { PageMeta } from '../content/pageMeta';
import {
  PAGE_SLUG,
  buildLocalizedSitePath,
  prefixPublicPath,
  publicPathForPageSlug,
} from './pageRouting';

export const FOOTER_LEGAL_PAGE_SLUGS = [PAGE_SLUG.impressum, PAGE_SLUG.datenschutz] as const;


export type FooterLegalPageSlug = (typeof FOOTER_LEGAL_PAGE_SLUGS)[number];

export interface FooterLegalLink {
  slug: FooterLegalPageSlug;
  label: string;
  href: string;
}

const FALLBACK_LABELS: Record<FooterLegalPageSlug, Record<string, string>> = {
  impressum: {
    de: 'Impressum',
    en: 'Legal notice',
    vi: 'Thông tin pháp lý',
  },
  datenschutz: {
    de: 'Datenschutz',
    en: 'Privacy',
    vi: 'Bảo mật',
  },
};

function pickLocalized(
  record: Record<string, string> | undefined,
  locale: string,
  defaultLocale: string,
): string {
  if (!record) {
    return '';
  }
  return record[locale] ?? record[defaultLocale] ?? Object.values(record)[0] ?? '';
}

function resolveLabel(
  slug: FooterLegalPageSlug,
  page: PageMeta | undefined,
  locale: string,
  defaultLocale: string,
): string {
  if (page) {
    const fromPage =
      pickLocalized(page.localizedTitle, locale, defaultLocale) ||
      pickLocalized(page.localizedTitle, defaultLocale, defaultLocale);
    if (fromPage) {
      return fromPage;
    }
  }
  return (
    FALLBACK_LABELS[slug][locale] ??
    FALLBACK_LABELS[slug][defaultLocale] ??
    FALLBACK_LABELS[slug].de ??
    slug
  );
}

/** Impressum + Datenschutz links for the global footer (system-root pages). */
export function resolveFooterLegalLinks(
  pages: PageMeta[] | undefined,
  locale: string,
  defaultLocale: string,
  publicPathPrefix?: string | null,
): FooterLegalLink[] {
  return FOOTER_LEGAL_PAGE_SLUGS.map((slug) => {
    const page = pages?.find((entry) => entry.slug === slug);
    return {
      slug,
      label: resolveLabel(slug, page, locale, defaultLocale),
      href: prefixPublicPath(
        buildLocalizedSitePath(publicPathForPageSlug(slug), locale, defaultLocale),
        publicPathPrefix,
      ),
    };
  });
}
